import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import AdministrationHeader from "../../components/header-components/AdministrationHeader.jsx";
import { useFinancialControlSummary, currentPeriod } from "./useFinancialControlSummary";
import { buildTiles, humanEUR, confidenceNote } from "./format";
import PeriodCaption from "./PeriodCaption";
import FilterDropdown from "../../components/ui-components/FilterDropdown";
import FreshnessLabel from "../../components/ui-components/FreshnessLabel";
import { HeaderPortal } from "../../components/ui-components/HeaderSlot";
import ControlKpiTile from "./ControlKpiTile";
import BudgetExceptionsTable from "./BudgetExceptionsTable";
import BudgetDetailDrawer from "./BudgetDetailDrawer";
import ExecutiveSummary from "./ExecutiveSummary";
import TotalSpendTile from "./TotalSpendTile";
import Tooltip from "../../components/form-components/Tooltip";
import InvestigateButton from "../investigations/components/InvestigateButton";

// Converts the page's "YYYY-MM" period into the {from,to} window the Investigation
// Workspace's Seed expects, so the seeded investigation matches what this page shows.
function periodToRange(period) {
  const start = dayjs(`${period}-01`);
  return { from: start.format("YYYY-MM-DD"), to: start.endOf("month").format("YYYY-MM-DD") };
}

// The reporting-period options — the last 12 months, newest first — as {value:"YYYY-MM",
// label:"June 2026"} for the FilterDropdown (the same dropdown used by the /approvals table).
function periodOptions() {
  return Array.from({ length: 12 }, (_, i) => {
    const d = dayjs().subtract(i, "month");
    return { value: d.format("YYYY-MM"), label: d.format("MMMM YYYY") };
  });
}

// FinancialControlPage is the executive entry point for the Economic Control
// module: one screen answering "are we in control of spend right now?".
const FinancialControlPage = () => {
  const [period, setPeriod] = useState(currentPeriod());
  const { data, isLoading, isError } = useFinancialControlSummary(period);
  const [selected, setSelected] = useState(null);
  const navigate = useNavigate();

  let body;
  if (isLoading) {
    body = (
      <div data-testid="fc-loading" className="mx-auto max-w-screen-2xl p-8">
        <div className="h-20 animate-pulse rounded-card bg-surface-2" />
        <div className="mt-5 grid grid-cols-5 gap-4">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="h-20 animate-pulse rounded-card bg-surface-2" />
          ))}
        </div>
      </div>
    );
  } else if (isError || !data) {
    body = (
      <div className="mx-auto max-w-screen-2xl p-8">
        <div className="rounded-card border border-danger bg-danger-soft px-5 py-4 text-sm text-danger">
          We couldn't load the financial control status right now. Please try again shortly.
        </div>
      </div>
    );
  } else {
    // Total spend leads the KPI row as its own tile (below); the AI tile is dropped (AI is already
    // inside the total and reads €0). Remaining tiles ordered so "vs plan" sits beside the figure.
    const builtTiles = buildTiles(data.kpis).filter((t) => t.key !== "ai");
    const tileOrder = ["budget", "spend", "unassigned", "biggest-mover"];
    const restTiles = tileOrder.map((k) => builtTiles.find((t) => t.key === k)).filter(Boolean);
    const spend = data.kpis.spend_vs_forecast || {};
    const hasTotalBudget = (data.kpis.budget_risk || {}).has_total_budget !== false;
    // Projection-range/confidence tooltip for the Total Spend tile's projected figure (honest,
    // n-gated labels driven by range_label + show_coverage — see C11).
    const TIP_RANGE_TEXT = { calibrated: "90% range", recent_errors: "range of recent errors", model: "model range", calibrating: "calibrating" };
    const rangeCaption = TIP_RANGE_TEXT[spend.range_label] || "likely range";
    const coverageNote =
      spend.show_coverage && spend.coverage != null
        ? ` · coverage ${Math.round(spend.coverage * 100)}% (target 90%, n=${spend.coverage_n})` +
          (spend.coverage_drift === "narrow" ? " — running narrow" : spend.coverage_drift === "wide" ? " — running wide" : "")
        : "";
    const bandNote =
      spend.range_label === "model" && spend.band_coverage != null && spend.band_coverage_n != null
        ? ` · covered ${Math.round(spend.band_coverage * 100)}% (n=${spend.band_coverage_n})`
        : "";
    const projTip =
      Number(spend.projection_low) > 0 && Number(spend.projection_high) > 0
        ? `${humanEUR(spend.forecast)} · ${rangeCaption} ${humanEUR(spend.projection_low)}–${humanEUR(spend.projection_high)}${coverageNote}${bandNote} · ${confidenceNote(spend)}`
        : confidenceNote(spend);
    const breachProbability = spend.breach_probability != null ? spend.breach_probability : null;
    const breachProvisional = breachProbability != null && spend.range_calibrated !== true;
    body = (
      <div className="mx-auto max-w-screen-2xl space-y-5 p-8">
        {/* Period selector + current-period caption + as-of freshness live on row 2
            (like the cost analyzer): the period picker, then a muted caption, then
            the read-only freshness stamp. */}
        <HeaderPortal slot="row2-right">
          <div className="flex items-center gap-2 whitespace-nowrap">
            <FilterDropdown
              ariaLabel="Reporting period"
              value={period}
              onChange={setPeriod}
              minWidth={160}
              height={32}
              variant="tertiary"
              options={periodOptions()}
            />
            <span aria-hidden style={{ width: 1, height: 16 }} className="bg-card-bd" />
            <PeriodCaption period={data.period} asOf={data.as_of} />
            {data.as_of ? (
              <>
                <span aria-hidden className="mx-0.5 text-[#C6CDD8] select-none">·</span>
                <FreshnessLabel>as of {dayjs(data.as_of).format("D MMM, HH:mm")}</FreshnessLabel>
              </>
            ) : null}
          </div>
        </HeaderPortal>
        {/* The verdict is folded into the executive summary (no separate heavy banner): the summary
            carries a subtle attention signal + inline action only when something needs it. */}
        <ExecutiveSummary
          prose={data.summary_prose}
          bullets={data.summary_bullets || []}
          overall={data.overall}
          confidence={data.kpis.forecast_confidence}
          onReview={(o) => {
            const budgets = (data.kpis.budget_risk || {}).budgets || [];
            // A budget-driven verdict opens that specific budget's drawer.
            if (o.driver === "budget" && o.primary_budget_id) {
              const b = budgets.find((x) => x.id === o.primary_budget_id);
              if (b) {
                setSelected(b);
                return;
              }
            }
            // Non-budget drivers route to where that signal lives.
            if (o.driver === "unassigned") {
              navigate("/allocation");
              return;
            }
            if (o.driver === "confidence") {
              navigate("/cost-analyzer");
              return;
            }
            // Total cloud & AI spend (and any other case) → open the whole-portfolio
            // total detail, never an unrelated budget.
            setSelected({ id: 0, name: "All cloud & AI spend" });
          }}
        />
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
          <TotalSpendTile
            actual={Number(spend.actual) || 0}
            forecast={Number(spend.forecast) || 0}
            projTip={projTip}
            momPct={spend.mom_pct}
            prevActual={spend.prev_month_actual}
            prevLabel={spend.prev_month_label}
            breachProbability={breachProbability}
            breachProvisional={breachProvisional}
            expectedOverage={spend.expected_overage != null ? Number(spend.expected_overage) : null}
            provisional={!!spend.provisional}
            provisionalReason={spend.provisional_reason}
            trend={data.spend_trend || []}
            forecastTrend={data.spend_forecast_trend || []}
            cap={hasTotalBudget ? Number(spend.total_cap) || null : null}
            onViewTotal={() => setSelected({ id: 0, name: "All cloud & AI spend" })}
          />
          {restTiles.map((t) => (
            <ControlKpiTile key={t.key} tile={t} />
          ))}
        </div>
        <div className="flex justify-end">
          <InvestigateButton
            label="Investigate spend"
            seed={{
              questionType: "total_spend",
              metric: "cost",
              scope: { kind: "total" },
              period: periodToRange(data.period),
              baseline: "prior_window",
            }}
          />
        </div>
        <BudgetExceptionsTable
          budgetRisk={data.kpis.budget_risk}
          period={data.period}
          asOf={data.as_of}
          onSelect={setSelected}
        />
        <BudgetDetailDrawer
          selected={selected}
          period={data.period}
          onClose={() => setSelected(null)}
          projectionLow={selected && selected.id === 0 ? Number(spend.projection_low) || null : null}
          projectionHigh={selected && selected.id === 0 ? Number(spend.projection_high) || null : null}
        />
        <p className="text-xs leading-snug text-text-2">
          Actual figures are month-to-date; &ldquo;projected&rdquo; figures extend current spend to month-end.
        </p>
      </div>
    );
  }

  // The Economic Control tab strip (Control / Ownership / Forecast) is mounted
  // per-page, like the sibling Ownership/Forecast pages. Render it in every state
  // so the tabs stay navigable while loading or on error.
  return (
    <>
      <AdministrationHeader
        title="Economic Control"
        className="text-text-3"
        icon={icon({ name: "display-chart-up", style: "light" })}
        filters={[]}
        showPeriodSelector={false}
      />
      {body}
      {/* Single shared react-tooltip instance for all data-tooltip-id="instantTooltip"
          anchors (KPI info glyph, owner cell). Rendered OUTSIDE the space-y-3 body so
          its inline node doesn't trigger sibling spacing and shift the page down. */}
      <Tooltip />
    </>
  );
};

export default FinancialControlPage;
